import { useCallback, useState } from "react";
import {
  approveSubmission,
  archivePathway,
  duplicatePathway,
  fetchPathwayDashboard,
  getPathway,
  listSubmissions,
  postPrompt,
  recordAttendance,
  sendEncouragement,
  sendSubmissionFeedback,
} from "./adminApi";
// Relative: a pure hook, resolvable under Jest without a shim.
import useAdminLoad from "../hooks/useAdminLoad";

/** The most addresses one `POST /encourage` accepts (contract §5.1). */
export const ENCOURAGE_BATCH = 500;
/** The most addresses one `POST /steps/{sid}/attendance` accepts (contract §10.4). */
export const ATTENDANCE_BATCH = 500;

/**
 * The attendance writes a Save makes (contract §10.4): the e-mails whose box changed,
 * grouped by their new value, `attended: true` first. Unchanged boxes are not sent.
 *
 * @param {string[]} stored   the e-mails the server has as attended (`live_sessions[].attended`)
 * @param {Set<string>|string[]} ticked  the e-mails ticked on screen now
 * @returns {{ emails: string[], attended: boolean }[]}  0, 1 or 2 groups, none empty
 */
export function attendanceChanges(stored, ticked) {
  const before = new Set(stored);
  const now = new Set(ticked);
  const add = [...now].filter((e) => !before.has(e));
  const remove = [...before].filter((e) => !now.has(e));
  return [
    ...(add.length ? [{ emails: add, attended: true }] : []),
    ...(remove.length ? [{ emails: remove, attended: false }] : []),
  ];
}

/**
 * The tracking dashboard of one pathway (contract §5.1, §10.6, spec screen 5): its
 * `PathwayDashboard` (totals, funnel, learners, `review_pending`, the cohort
 * `live_sessions` with their attendance), its `AdminPathway` (schedule, status and the
 * weekly prompt thread, which the dashboard shape does not carry) and its review queue
 * (the PENDING `ReviewItem`s, contract §10.3), read together.
 *
 * ONE AUTHORITY. Figures are never patched here: after an archive both reads are
 * repeated, because only the server's evaluator (contract §3) knows how a write moves
 * them. Posting a prompt replaces the AdminPathway with the server's answer (its thread
 * holds the new prompt) and moves no figure. Encouragement moves nothing either. A review
 * (approve, feedback) or an attendance save re-reads everything: it moves the pending
 * count, the queue, the funnel and the learners' progress, all of them the server's.
 *
 * Writes REJECT with the raw axios error; the screen words it (pathways/errors.js).
 *
 * @param {string} id pathway id
 */
export default function usePathwayDashboard(id) {
  const [dashboard, setDashboard] = useState(null);
  const [pathway, setPathway] = useState(null);
  const [reviews, setReviews] = useState(null);
  const load = useCallback(async () => {
    const [d, p, r] = await Promise.all([fetchPathwayDashboard(id), getPathway(id), listSubmissions(id, "pending")]);
    setDashboard(d);
    setPathway(p);
    setReviews(r);
  }, [id]);
  const { loading, loadError, reload } = useAdminLoad(load);

  /**
   * `{ emails, message }` -> `{ sent, skipped }`. The endpoint takes at most
   * ENCOURAGE_BATCH addresses per call (contract §5.1), so a longer list is sent in
   * consecutive batches and the counts are summed. A refused batch rejects, and the
   * batches before it stay sent.
   */
  const encourage = useCallback(async ({ emails, message }) => {
    const total = { sent: 0, skipped: 0 };
    for (let i = 0; i < emails.length; i += ENCOURAGE_BATCH) {
      // Sequential on purpose: one practitioner action, not a burst of parallel mails.
      // eslint-disable-next-line no-await-in-loop
      const r = await sendEncouragement(id, { emails: emails.slice(i, i + ENCOURAGE_BATCH), message });
      total.sent += r.sent;
      total.skipped += r.skipped;
    }
    return total;
  }, [id]);

  const prompt = useCallback(async (text) => {
    setPathway(await postPrompt(id, { text }));
  }, [id]);

  const archive = useCallback(async () => {
    await archivePathway(id);
    await reload();
  }, [id, reload]);

  /** Resolves the NEW draft (the screen opens it in the builder). */
  const duplicate = useCallback((lang) => duplicatePathway(id, { lang }), [id]);

  /** Approve one pending submission; the screen re-reads (the queue and its count move). */
  const approve = useCallback(async (submissionId) => {
    await approveSubmission(id, submissionId);
    await reload();
  }, [id, reload]);

  /** Send feedback on one pending submission (`message` trimmed by the caller). */
  const feedback = useCallback(async (submissionId, message) => {
    await sendSubmissionFeedback(id, submissionId, { message });
    await reload();
  }, [id, reload]);

  /**
   * Saves the attendance of one cohort live step: `attendanceChanges(stored, ticked)`,
   * each group in batches of ATTENDANCE_BATCH, then one re-read. A refused batch rejects,
   * and the batches before it stay written (the re-read still runs, so the screen shows
   * what the server holds). Resolves the number of e-mails sent, 0 when nothing changed.
   */
  const saveAttendance = useCallback(async (stepId, stored, ticked) => {
    const groups = attendanceChanges(stored, ticked);
    if (groups.length === 0) return 0;
    let sent = 0;
    try {
      for (const { emails, attended } of groups) {
        for (let i = 0; i < emails.length; i += ATTENDANCE_BATCH) {
          const batch = emails.slice(i, i + ATTENDANCE_BATCH);
          // Sequential on purpose: each batch completes steps and emits events server-side.
          // eslint-disable-next-line no-await-in-loop
          await recordAttendance(id, stepId, { emails: batch, attended });
          sent += batch.length;
        }
      }
    } finally {
      await reload();
    }
    return sent;
  }, [id, reload]);

  return {
    dashboard, pathway, reviews, loading, error: loadError, reload,
    encourage, prompt, archive, duplicate, approve, feedback, saveAttendance,
  };
}
